import { FieldPath, useFormContext } from "react-hook-form";
import {
  ModelScreenerColumnSettingData,
  ModelScreenerColumnSettingDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import { useMemo } from "react";
import { ModelScreenerMainHeader } from "@models/model-screener/entities/modelScreenerData";
import { MSColumnSettingsDefaultValueConstants } from "./constants";

// Hooks
export const useMSTableColumnSettingVisibility = ({
  mainHeaderId,
  subHeaderId,
}: {
  mainHeaderId: string;
  subHeaderId?: string;
}) => {
  const { getValues, watch, setValue } =
    useFormContext<ModelScreenerColumnSettingDTO>();
  const watchedFormData = watch();

  const currentMainHeaderFieldPath = useMemo(
    () => getCurrentMainHeaderFieldPathById(mainHeaderId, watchedFormData),
    [mainHeaderId, watchedFormData],
  );

  const currentSubHeaderFieldPath = useMemo(
    () =>
      subHeaderId
        ? getCurrentSubHeaderFieldPathById(
            mainHeaderId,
            subHeaderId,
            watchedFormData,
          )
        : null,
    [mainHeaderId, subHeaderId, watchedFormData],
  );

  const watchedIsMainHeaderVisible: boolean = useMemo(() => {
    if (!currentMainHeaderFieldPath) return false;
    return !!getValues(currentMainHeaderFieldPath.isVisible);
  }, [currentMainHeaderFieldPath, getValues]);

  const isAnySubHeaderVisible: boolean = useMemo(() => {
    if (!currentMainHeaderFieldPath) return false;
    const subHeaders =
      (getValues(
        currentMainHeaderFieldPath.columnSettings,
      ) as ModelScreenerColumnSettingData[]) || [];
    return subHeaders.some(
      (subHeader: ModelScreenerColumnSettingData) => subHeader.isVisible,
    );
  }, [currentMainHeaderFieldPath, getValues]);

  const watchedIsSubHeaderVisible: boolean = useMemo(() => {
    if (!currentSubHeaderFieldPath) return false;
    return !!getValues(currentSubHeaderFieldPath.isVisible);
  }, [currentSubHeaderFieldPath, getValues]);

  return {
    isMainHeaderVisible: watchedIsMainHeaderVisible,
    isSubHeaderVisible: watchedIsSubHeaderVisible,
    isAnySubHeaderVisible,
    currentSubHeaderFieldPath,
    currentMainHeaderFieldPath,
    setValue,
    getValues,
  };
};

// Paths
export type EditMSColumnSettingFieldPath =
  FieldPath<ModelScreenerColumnSettingDTO>;
export type EditMSColumnSettingDataFieldPath = {
  order: EditMSColumnSettingFieldPath;
  isVisible: EditMSColumnSettingFieldPath;
  columnSettings: EditMSColumnSettingFieldPath;
};

// Functions
// Get field paths
const getEditMSColumnFieldPath = (
  path: string,
): EditMSColumnSettingDataFieldPath => {
  return {
    order: `${path}.order` as EditMSColumnSettingFieldPath,
    isVisible: `${path}.isVisible` as EditMSColumnSettingFieldPath,
    columnSettings: `${path}.columnSettings` as EditMSColumnSettingFieldPath,
  };
};

const getEditMSColumnMainHeaderFieldPath = (
  mainHeaderIndex: number,
): EditMSColumnSettingDataFieldPath => {
  return getEditMSColumnFieldPath(`data.${mainHeaderIndex}`);
};

const getEditMSColumnSubHeaderFieldPath = (
  mainHeaderColumnSettingsPath: string,
  subHeaderIndex: number,
): EditMSColumnSettingDataFieldPath => {
  return getEditMSColumnFieldPath(
    `${mainHeaderColumnSettingsPath}.${subHeaderIndex}`,
  );
};

export const getCurrentMainHeaderFieldPathById = (
  mainHeaderId: string,
  formData: ModelScreenerColumnSettingDTO,
): EditMSColumnSettingDataFieldPath => {
  const headerIndex = formData?.data?.findIndex(
    (headerItem) => headerItem.id === mainHeaderId,
  );

  return getEditMSColumnMainHeaderFieldPath(headerIndex);
};

export const getCurrentSubHeaderFieldPathById = (
  mainHeaderId: string,
  subHeaderId: string,
  formData: ModelScreenerColumnSettingDTO,
): EditMSColumnSettingDataFieldPath | null => {
  let result: EditMSColumnSettingDataFieldPath | null = null;

  formData.data?.forEach((mainHeader, mainHeaderIndex) => {
    if (mainHeader.id !== mainHeaderId) return;

    const mainHeaderFieldPath =
      getEditMSColumnMainHeaderFieldPath(mainHeaderIndex);
    const subHeaders = mainHeader.columnSettings || [];
    subHeaders?.forEach((subHeader, subHeaderIndex) => {
      if (subHeader.id !== subHeaderId) return;

      result = getEditMSColumnSubHeaderFieldPath(
        mainHeaderFieldPath.columnSettings,
        subHeaderIndex,
      );
    });
  });

  return result;
};

export const generateDefaultColumnSetting = (
  modelScreenerHeaderData: ModelScreenerMainHeader[],
): ModelScreenerColumnSettingDTO => {
  return {
    id: MSColumnSettingsDefaultValueConstants.DefaultColumnSettingId,
    name: MSColumnSettingsDefaultValueConstants.DefaultColumnSettingName,
    isUserDefault: false,
    isSystemDefault: true,
    data: modelScreenerHeaderData
      .slice(MSColumnSettingsDefaultValueConstants.IgnoredHeaderColumnCount)
      .map((headerItem) => {
        const subData: ModelScreenerColumnSettingData[] =
          headerItem.subHeaders.map((subHeaderItem) => ({
            id: subHeaderItem.id,
            name: subHeaderItem.name,
            subHeaderId: subHeaderItem.id,
            parentId: headerItem.id,
            order: subHeaderItem.order,
            isVisible: !subHeaderItem.hiddenByDefault,
          }));

        return {
          id: headerItem.id,
          name: headerItem.name,
          headerId: headerItem.id,
          order: headerItem.order,
          isVisible: subData.some(({ isVisible }) => isVisible),
          columnSettings: subData,
        };
      }),
  };
};
